import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { PageHead, StatusBadge } from "@/pages/AppLayout";
import { UpgradeNudge } from "@/components/UpgradeNudge";
import { toast } from "sonner";
import PlateScanner from "@/components/PlateScanner";
import { Plus, Search, Wrench, ArrowUpRight, QrCode, CalendarClock } from "lucide-react";

const FILTERS = ["all", "overdue", "upcoming", "healthy"];

function regoBadge(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const days = Math.ceil((d - new Date()) / 86400000);
  let cls, text;
  if (days < 0) { cls = "bg-[#FF3B30] text-white"; text = "REGO EXPIRED"; }
  else if (days <= 30) { cls = "bg-[#FFB800] text-black"; text = `REGO ${days}d`; }
  else { cls = "bg-white/5 text-[#94A3B8] border border-white/10"; text = `REGO ${days}d`; }
  return { cls, text };
}

export default function Machines() {
  const nav = useNavigate();
  const [machines, setMachines] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortOverdue, setSortOverdue] = useState(true);

  useEffect(() => { api.get("/machines").then(({ data }) => setMachines(data)); }, []);

  const onPlate = (data) => {
    if (!data.plate) return;
    if (data.matches.length === 1) {
      toast.success(`Found ${data.matches[0].name} (${data.plate})`);
      nav(`/machines/${data.matches[0].id}`);
    } else if (data.matches.length > 1) {
      toast.info(`Plate ${data.plate} matches ${data.matches.length} machines — showing them.`);
      setQ(data.plate);
    } else {
      toast.warning(`Read plate ${data.plate}, but no machine has that rego.`);
      setQ(data.plate);
    }
  };

  const shown = useMemo(() => {
    if (!machines) return [];
    let list = machines.filter((m) =>
      `${m.make} ${m.model} ${m.type} ${m.fleet_number || ""} ${m.serial} ${m.rego_number}`.toLowerCase().includes(q.toLowerCase())
    );
    if (filter !== "all") list = list.filter((m) => m.health === filter);
    if (sortOverdue) {
      const rank = { overdue: 0, upcoming: 1, healthy: 2 };
      list = [...list].sort((a, b) => rank[a.health] - rank[b.health]);
    }
    return list;
  }, [machines, q, filter, sortOverdue]);

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="machines-page">
      <PageHead title="Machines" sub="Every machine in the fleet.">
        <PlateScanner onResult={onPlate} label="Scan Plate" testid="machines-scan-plate" />
        <a
          href={`${API}/fleet/qr-sheet.pdf?token=${localStorage.getItem("ironlog_token")}`}
          target="_blank" rel="noreferrer"
          className="btn-ghost h-12 px-4 font-head flex items-center gap-2"
          data-testid="qr-sheet-btn"
        ><QrCode className="w-4 h-4" /> QR Sheet</a>
        <Link to="/machines/new" className="btn-signal h-12 px-5 font-head flex items-center gap-2" data-testid="add-machine-btn"><Plus className="w-4 h-4" /> Add Machine</Link>
      </PageHead>

      <UpgradeNudge kind="machine" />

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#94A3B8] absolute left-4 top-1/2 -translate-y-1/2" />
          <input className="il-input pl-11" placeholder="Search make, model, fleet no., serial, rego…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="machine-search" />
        </div>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`h-13 px-4 font-head font-600 uppercase text-sm tracking-wide border transition-colors ${filter === f ? "bg-[#FFB800] text-black border-[#FFB800]" : "card text-[#94A3B8] border-white/20 hover:text-white"}`} style={{ height: "3.25rem" }} data-testid={`filter-${f}`}>{f}</button>
          ))}
        </div>
      </div>

      {machines === null ? (
        <p className="font-head text-[#FFB800] uppercase tracking-widest">Loading…</p>
      ) : shown.length === 0 ? (
        <div className="card p-12 text-center">
          <Wrench className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="font-body text-[#94A3B8]">No machines match. Add one to get started.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((m) => (
            <Link to={`/machines/${m.id}`} key={m.id} className="card hover:border-[#FFB800]/40 transition-colors group" data-testid={`machine-card-${m.id}`}>
              <div className="h-40 bg-[#0D0E11] overflow-hidden relative">
                {m.has_photo ? <img src={`${API}/public/machines/${m.id}/photo`} alt={m.model} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Wrench className="w-12 h-12 text-white/10" /></div>}
                <div className="absolute top-3 right-3"><StatusBadge status={m.health} /></div>
                {m.fleet_number && <div className="absolute top-3 left-3 bg-[#0D0E11]/80 border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-[#FFB800]" data-testid={`fleet-no-${m.id}`}>{m.fleet_number}</div>}
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#FFB800]">{m.type}</p>
                  <ArrowUpRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#FFB800] transition-colors" />
                </div>
                <p className="font-head font-800 text-2xl uppercase tracking-tight mt-1 leading-none">{m.make} {m.model}</p>
                <div className="flex items-center gap-4 mt-3 font-mono text-xs text-[#94A3B8]">
                  <span>{m.running_hours || 0}h</span>
                  {m.odometer_km ? <span>{m.odometer_km} km</span> : null}
                  {m.rego_number && <span>REGO {m.rego_number}</span>}
                </div>
                {(() => { const b = regoBadge(m.rego_renewal_date); return b ? (
                  <span className={`inline-flex items-center gap-1 mt-2 px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${b.cls}`} data-testid={`rego-badge-${m.id}`}>
                    <CalendarClock className="w-3 h-3" /> {b.text}
                  </span>
                ) : null; })()}
                {(m.overdue_count > 0 || m.upcoming_count > 0) && (
                  <p className="font-body text-xs text-[#94A3B8] mt-2">{m.overdue_count} overdue · {m.upcoming_count} upcoming</p>
                )}
                <p className="font-body text-[10px] text-[#94A3B8] mt-3 border-t border-white/10 pt-2">Added by {m.added_by_name}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
